import { NodeViewSettingsPanel, type NodeViewSettingsPanelField } from '@/components/common/node-view-settings-panel';
import { CommonNodeViewWrapper } from '@/components/common/node-view-wrapper';
import { Tooltip, TooltipContent, TooltipTrigger } from '@/components/tiptap-editor/tiptap-ui-primitive/tooltip/tooltip';
import { Button } from '@/components/ui/button';
import { useIsEditorReadonly } from '@/hooks/use-is-editor-readonly';
import { useLazyMountWhenVisible } from '@/hooks/use-lazy-mount-when-visible';
import { zyServer } from '@/lib/servers';
import { getOrCreateZyStudioResourceToken } from '@/lib/zybooks-api';
import authService from '@/services/zybook-auth-service';
import { CodingRooms } from '@exlinc/cr-zystudio-sdk';
import type { IDEOptions } from '@exlinc/cr-zystudio-sdk/models';
import type { ReactNodeViewProps } from '@tiptap/react';
import { NodeViewContent } from '@tiptap/react';
import { Info } from 'lucide-react';
import { memo, useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { v4 as uuidv4 } from 'uuid';

type ContentResourceJSON = {
    id: number;
    guid: string;
    release_id: number;
    caption?: string;
    activity_type?: string | null;
    instructions?: string;
    payload?: {
        testCases?: unknown;
        options?: IDEOptions;
    };
};

const ZyStudioNodeView: React.FC<ReactNodeViewProps> = (props: ReactNodeViewProps) => {
    const { node, updateAttributes, selected, editor, decorations } = props;
    const isEditorReadonly = useIsEditorReadonly(editor);
    const caption = node.attrs.caption || '';
    const guid = node.attrs.guid || '';
    const activityType = node.attrs.activityType || 'tinker';
    const [isLoading, setIsLoading] = useState(false);
    const [isCreatingNewResource, setIsCreatingNewResource] = useState(false);
    const [isSaving, setIsSaving] = useState(false);
    const [error, setError] = useState<string | null>(null);
    const [createResourceError, setCreateResourceError] = useState<string | null>(null);
    const isLabEditMode = node.attrs.isLabEditMode === true;

    const containerRef = useRef<HTMLDivElement>(null);
    const crClientRef = useRef<CodingRooms | null>(null);
    const contentResourceJSONRef = useRef<ContentResourceJSON | null>(null);
    const abortRef = useRef<AbortController | null>(null);
    const initialAccessRef = useRef<'ro' | 'rw'>('ro');
    const shouldMount = useLazyMountWhenVisible({
        containerRef,
        editor,
        forceMount: selected || isLabEditMode,
    });

    const disableEditLabButton = !guid || isLoading;
    const disableActivityTypeSelection = true;

    const initializeCodingRooms = useCallback(
        async (access: 'ro' | 'rw') => {
            if (!containerRef.current || !guid) {
                return;
            }

            abortRef.current?.abort();
            const controller = new AbortController();
            abortRef.current = controller;

            const releaseId = editor.$doc.attributes.releaseId ? parseInt(editor.$doc.attributes.releaseId, 10) : null;
            if (!releaseId) {
                throw new Error('No release number found for this Advanced Lab');
            }
            setIsLoading(true);
            setError(null);

            try {
                const contentResourceResponse = await zyServer.get(`content_resources/${guid}`, { params: { dev: true } });
                if (controller.signal.aborted) return;

                if (!contentResourceResponse.success) {
                    throw new Error('Failed to fetch content resource for zyStudio');
                }
                if (contentResourceResponse.content_resources.length === 0) {
                    throw new Error('No content resource found for the provided GUID');
                }

                const contentResource = contentResourceResponse.content_resources.sort(
                    (a: ContentResourceJSON, b: ContentResourceJSON) => b.release_id - a.release_id,
                )[0] as ContentResourceJSON;

                contentResourceJSONRef.current = contentResource;

                const crClient = new CodingRooms(containerRef.current, {
                    debug: false,
                    autosave: false,
                    isDarkMode: false,
                    url: import.meta.env.VITE_CODING_ROOMS_URL,
                    onGradingDataChanges: testCases => {
                        const resource = contentResourceJSONRef.current;
                        if (resource?.payload) {
                            contentResourceJSONRef.current = {
                                ...resource,
                                payload: { ...resource.payload, testCases },
                            };
                        }
                    },
                    onIDEOptionsChanges: options => {
                        const resource = contentResourceJSONRef.current;
                        if (resource?.payload) {
                            contentResourceJSONRef.current = {
                                ...resource,
                                payload: { ...resource.payload, options },
                            };
                        }
                    },
                    onInstructionsGenerated: instructionsPayload => {
                        const resource = contentResourceJSONRef.current;
                        if (resource) {
                            contentResourceJSONRef.current = {
                                ...resource,
                                instructions: instructionsPayload.instructions,
                            };
                        }
                    },
                });
                crClientRef.current = crClient;

                const { token, aci_token } = await getOrCreateZyStudioResourceToken({
                    access,
                    actor: 'instructor',
                    content_resource_id: contentResource.id,
                    // @ts-expect-error - accessing private field
                    frame_id: crClient.frameId,
                    guid: guid,
                    kinds: 'template,model-solution',
                    release_id: contentResource.release_id,
                    subject_id: '0',
                    zybook_id: '0',
                });

                if (controller.signal.aborted) return;

                const userId = authService.getUserId();
                if (!userId) {
                    throw new Error('Failed to get user ID');
                }
                crClient.init(token, userId, { aciToken: aci_token });
                setIsLoading(false);
            } catch (err) {
                if (!controller.signal.aborted) {
                    setError(err instanceof Error ? err.message : 'Failed to initialize CodingRooms');
                    setIsLoading(false);
                }
            }
        },
        [editor.$doc.attributes.releaseId, guid],
    );

    const teardown = () => {
        abortRef.current?.abort();
        abortRef.current = null;
        crClientRef.current?.dispose();
        crClientRef.current = null;
        contentResourceJSONRef.current = null;
        containerRef.current?.querySelector('iframe')?.remove();
    };

    useEffect(() => {
        if (!shouldMount || !guid) {
            return;
        }

        const access = initialAccessRef.current;
        initialAccessRef.current = 'ro';
        updateAttributes({ isLabEditMode: access === 'rw' });
        void initializeCodingRooms(access);
        return teardown;
    }, [guid, shouldMount, updateAttributes, initializeCodingRooms]);

    const handleCaptionChange = useCallback(
        (e: React.ChangeEvent<HTMLInputElement>) => {
            updateAttributes({ caption: e.target.value });
        },
        [updateAttributes],
    );

    const handleActivityTypeChange = useCallback(
        (e: React.ChangeEvent<HTMLSelectElement>) => {
            updateAttributes({ activityType: e.target.value });
        },
        [updateAttributes],
    );

    const updateContentResource = async () => {
        const contentResourceJSON = contentResourceJSONRef.current;
        if (!contentResourceJSON) {
            return;
        }
        const params = { ...contentResourceJSON, caption, activity_type: activityType === 'lab' ? 'lab' : null };
        const { type: _type, ...updateParams } = params as ContentResourceJSON & { type?: string }; // eslint-disable-line @typescript-eslint/no-unused-vars -- type is not used in the updateParams
        await zyServer.put(`content_resource/${contentResourceJSON.id}`, { body: updateParams });
    };

    const handleSave = async () => {
        const crClient = crClientRef.current;
        if (!crClient) {
            return;
        }

        setIsSaving(true);
        setError(null);

        try {
            await crClient.flushUserData();
            await crClient.saveOptionsTestCases();
            await updateContentResource();
            updateAttributes({ isLabEditMode: false });
            teardown();
            void initializeCodingRooms('ro');
        } catch (err) {
            setError(err instanceof Error ? err.message : 'Error saving resource data');
        } finally {
            setIsSaving(false);
        }
    };

    const handleEnterEditLab = () => {
        updateAttributes({ isLabEditMode: true });
        setError(null);
        teardown();
        void initializeCodingRooms('rw');
    };

    const handleCreateNew = async () => {
        const newGuid = uuidv4();
        setIsLoading(true);
        setCreateResourceError(null);
        try {
            setIsCreatingNewResource(true);
            await createZyStudioResource(newGuid);
            initialAccessRef.current = 'rw';
            updateAttributes({ guid: newGuid });
        } catch (err) {
            setCreateResourceError(err instanceof Error ? err.message : 'Failed to create zystudio resource');
            setIsLoading(false);
        } finally {
            setIsCreatingNewResource(false);
        }
    };

    const createZyStudioResource = async (guid: string) => {
        const releaseId = editor.$doc.attributes.releaseId;
        if (!releaseId) {
            throw new Error('Invalid release ID');
        }
        const response = await zyServer.post('content_resource', {
            body: {
                type: 'zystudio',
                release_id: editor.$doc.attributes.releaseId,
                guid,
                payload: {},
                caption: caption || 'New zyStudio Resource',
                activity_type: activityType === 'lab' ? 'lab' : null,
                parts: 0,
            },
        });
        if (!response.success) {
            throw new Error('Failed to create zystudio content resource');
        }
    };

    const settingsPanelFields = useMemo(() => {
        return [
            {
                label: 'Caption',
                type: 'text',
                value: caption,
                onChange: handleCaptionChange,
                placeholder: 'Caption...',
                searchAttr: 'caption',
            },
            {
                label: 'Type',
                type: 'select',
                value: activityType,
                onChange: handleActivityTypeChange,
                disabled: disableActivityTypeSelection,
                options: [
                    { value: 'lab', label: 'Lab' },
                    { value: 'tinker', label: 'Tinker' },
                ],
                searchAttr: 'activityType',
            },
        ] as NodeViewSettingsPanelField[];
    }, [caption, activityType, disableActivityTypeSelection, handleCaptionChange, handleActivityTypeChange]);

    const mainContent = (
        <>
            <NodeViewContent />
            <div
                ref={containerRef}
                className="min-h-[600px] h-[600px] rounded border border-gray-300 bg-gray-50"
                contentEditable={false}
            >
                {!guid && (
                    <div className="flex h-[600px] flex-col items-center justify-center">
                        <p className="text-gray-500">Create a new zyStudio resource</p>
                        <Button
                            className="mt-5"
                            onClick={handleCreateNew}
                            disabled={isCreatingNewResource || isEditorReadonly}
                            isLoading={isCreatingNewResource}
                        >
                            Create New Resource
                        </Button>
                        <p className="text-red-500">{createResourceError}</p>
                    </div>
                )}
                {guid && shouldMount && isLoading && (
                    <div className="flex h-[600px] items-center justify-center text-gray-500">Loading zyStudio environment...</div>
                )}
                {guid && error && <div className="flex h-[600px] items-center justify-center text-red-500">Error: {error}</div>}
            </div>
        </>
    );

    return (
        <CommonNodeViewWrapper
            decorations={decorations}
            label="zyStudio"
            selected={selected}
            settingsPanel={
                <NodeViewSettingsPanel fields={settingsPanelFields}>
                    <div className="flex items-center gap-2 mt-2">
                        {!isLabEditMode ? (
                            <Button
                                type="button"
                                className="cursor-pointer"
                                onClick={handleEnterEditLab}
                                disabled={disableEditLabButton || isEditorReadonly}
                                contentEditable={false}
                            >
                                Edit Lab
                            </Button>
                        ) : (
                            <div className="flex items-center gap-2">
                                <Button
                                    type="button"
                                    className="cursor-pointer"
                                    onClick={handleSave}
                                    isLoading={isSaving}
                                    disabled={isEditorReadonly}
                                    contentEditable={false}
                                >
                                    Done Editing
                                </Button>
                            </div>
                        )}
                        <Tooltip
                            delay={0}
                            closeDelay={0}
                        >
                            <TooltipTrigger asChild>
                                <button
                                    type="button"
                                    className="rounded p-1 text-gray-500 transition-colors hover:bg-gray-200 hover:text-gray-700"
                                    contentEditable={false}
                                    aria-label="Done Editing save details"
                                >
                                    <Info className="h-4 w-4" />
                                </button>
                            </TooltipTrigger>
                            <TooltipContent>
                                Instructions and template changes are auto-saved. All other changes must be manually saved.
                            </TooltipContent>
                        </Tooltip>
                    </div>
                </NodeViewSettingsPanel>
            }
            contentClassName="px-4 py-4"
            fullscreenContent={mainContent}
            fullscreenMode="inline"
            contentHeightClassName=""
            containerClassName="bg-white dark:bg-black"
        >
            {mainContent}
        </CommonNodeViewWrapper>
    );
};

export default memo(ZyStudioNodeView);
